import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import React from 'react'
import styled from 'styled-components'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

dayjs.extend(customParseFormat)
dayjs.extend(relativeTime)

const formatRelative = (mmddyyyy: string): string => {
  if (mmddyyyy === 'N/A') return mmddyyyy
  const rel = dayjs(mmddyyyy, 'MM/DD/YYYY').fromNow()
  return rel.charAt(0).toUpperCase() + rel.slice(1)
}

const formatShort = (mmddyyyy: string): string =>
  mmddyyyy === 'N/A' ? mmddyyyy : dayjs(mmddyyyy, 'MM/DD/YYYY').format('M/D/YY')

import { Sell } from '@mui/icons-material'
import { Chiclet } from 'sutra/components/Chiclet/Chiclet'

import { Emoji } from 'components'
import { SourceIcon } from 'features/products/components/ProductSourceIcon'
import { Product } from 'features/products/types'

type Props = {
  product: Product
}

export const ProductCard: React.FC<Props> = ({ product }) => {
  const customFields = Object.entries(product.custom_fields ?? {}).map(
    ([field, value]) => ({
      field,
      value: typeof value === 'object' ? JSON.stringify(value) : String(value),
    }),
  )

  return (
    <Card>
      <Header>
        <ProductName>{product.name}</ProductName>
        {product.generic_listing && (
          <Badge>
            <Emoji symbol="⭐" /> General Listing
          </Badge>
        )}
      </Header>

      <ImageArea>
        {product.image_url ? (
          <img src={product.image_url} height="100%" width="auto" alt="" />
        ) : (
          <Sell fontSize="large" color="disabled" />
        )}
      </ImageArea>

      <Divider />

      <StatsList>
        {product.review_count === 0 ? (
          <StatRow>
            <StatLabel>Records</StatLabel>
            <StatValue>{product.review_count.toLocaleString()}</StatValue>
          </StatRow>
        ) : (
          <>
            <StatRow>
              <StatLabel>Reviews</StatLabel>
              <StatValue>{product.review_count.toLocaleString()}</StatValue>
            </StatRow>
            <StatRow>
              <StatLabel>Ratings</StatLabel>
              <StatValue>
                {product.rating_count != null
                  ? product.rating_count.toLocaleString()
                  : '—'}
              </StatValue>
            </StatRow>
            {product.first_review && (
              <StatRow>
                <StatLabel>First review</StatLabel>
                <Tooltip
                  content={formatShort(product.first_review)}
                  side="right"
                >
                  <StatValue>{formatRelative(product.first_review)}</StatValue>
                </Tooltip>
              </StatRow>
            )}
            {product.last_review && (
              <StatRow>
                <StatLabel>Last review</StatLabel>
                <Tooltip
                  content={formatShort(product.last_review)}
                  side="right"
                >
                  <StatValue>{formatRelative(product.last_review)}</StatValue>
                </Tooltip>
              </StatRow>
            )}
          </>
        )}
      </StatsList>

      <SourcesArea>
        <SectionLabel>Sources</SectionLabel>
        {product.sources && product.sources.length > 0 ? (
          product.sources.every((s) => !s.source_url) ? (
            <SyndicatedBlock>
              <Tooltip
                content={
                  <span>
                    Reviews are <strong>syndicated</strong> — aggregated from
                    retailer partner sites rather than collected directly from
                    this URL.
                  </span>
                }
              >
                <span style={{ cursor: 'default', width: 'fit-content' }}>
                  <Chiclet $tone="cerulean">Syndicated</Chiclet>
                </span>
              </Tooltip>
              {(() => {
                const sites = [
                  ...new Set(
                    product.sources
                      .map((s) => s.syndicated_source)
                      .filter(Boolean),
                  ),
                ]
                return sites.length > 0 ? (
                  <SyndicatedSites>{sites.join(', ')}</SyndicatedSites>
                ) : null
              })()}
            </SyndicatedBlock>
          ) : (
            <SourceRow>
              {product.sources.map((source) => (
                <SourceIcon key={source.uuid} source={source} />
              ))}
            </SourceRow>
          )
        ) : (
          <StatRow>
            <StatValue>None available</StatValue>
          </StatRow>
        )}
      </SourcesArea>

      <DetailsArea>
        {customFields.length > 0 && (
          <>
            <SectionLabel>Product Details</SectionLabel>
            {customFields.map(({ field, value }) => (
              <StatRow key={field}>
                <DetailLabel>{field}</DetailLabel>
                <StatValue>{value}</StatValue>
              </StatRow>
            ))}
          </>
        )}
      </DetailsArea>
    </Card>
  )
}

const Card = styled.div`
  min-width: 260px;
  max-width: 260px;
  background: white;
  border: 1px solid rgb(216, 216, 216);
  border-radius: 8px;
  box-shadow:
    rgba(0, 0, 0, 0.02) 0px 3px 6px -2px,
    rgba(0, 0, 0, 0.04) 0px 1px 1px;
  padding: 10px;
  display: grid;
  grid-template-rows: subgrid;
  /* Pin the single column to the card. Left implicit it is auto-sized, so its
     base size is the widest child's min-content contribution — a nowrap
     custom-field key then stretches every row past the card's 238px content
     box and under the neighbouring card. */
  grid-template-columns: minmax(0, 1fr);
  grid-row: 1 / -1;
  row-gap: 6px;
  font-size: 10px;
  line-height: 1.4;
  flex-shrink: 0;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
`

const ProductName = styled.div`
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-clamp: 2;
  color: #333;
`

const Badge = styled.div`
  font-size: 9px;
  color: #888;
  display: flex;
  align-items: center;
  gap: 2px;
`

const ImageArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 140px;
  overflow: hidden;
`

const Divider = styled.div`
  height: 1px;
  background: #f0f0f0;
  margin: 2px 0;
`

const StatsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 6px;
  font-size: 13px;
  border-bottom: 1px solid #f5f5f5;
  padding: 2px 0;
`

const StatLabel = styled.span`
  color: #999;
  white-space: nowrap;
  font-weight: 400;
  letter-spacing: -0.02em;
`

const StatValue = styled.span`
  color: #333;
  text-align: right;
  overflow-wrap: anywhere;
`

// Custom-field keys are customer data and run long ("Flavor Format Category
// Report Long Description" is 46 chars). StatLabel's nowrap would make that the
// row's minimum width, so these wrap instead.
const DetailLabel = styled(StatLabel)`
  white-space: normal;
  overflow-wrap: anywhere;
`

const SectionLabel = styled.div`
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #aaa;
  margin-top: 4px;
`

const SyndicatedBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const SyndicatedSites = styled.div`
  font-size: 10px;
  color: #aaa;
  line-height: 1.4;
`

const SourceRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

const SourcesArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const DetailsArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`
